import React from "react";
import styled, { keyframes, css } from "styled-components";

const FluidBg = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /* Content scrolls normally; background layers are fixed when parallax=true */
  min-height: calc(100svh);
  overflow: hidden;
  isolation: isolate;

  ${({ $normalHeight }) =>
    $normalHeight &&
    css`
      min-height: unset;
      height: auto;
    `}
`;

/* Layer positioning helper: fixed for parallax, absolute otherwise */
const LayerBase = css`
  position: ${(p) => (p.$parallax ? "fixed" : "absolute")};
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  pointer-events: none;
`;

const FadeLayer = css`
  ${LayerBase};
  transition: opacity 800ms ease;
  opacity: ${(p) => (p.$show ? 1 : 0)};
  will-change: opacity;
`;

/* Night gradient */
const GradientNight = styled.div`
  ${FadeLayer};
  z-index: 0;
  background: linear-gradient(
    to bottom,
    #020718 0%,
    #0b132b 25%,
    #0a0f20 55%,
    #2a1007 100%
  );
`;

/* Day gradient (warm sky → peachy orange) */
const GradientDay = styled.div`
  ${FadeLayer};
  z-index: 0;
  background: linear-gradient(
    to bottom,
    #87c5ff 0%,
    #bfe3ff 35%,
    #def2ff 70%,
    #ffefe3 100%
  );
`;

/* Subtle drifting bokeh (night only here) */
const bokehFloat = keyframes`
  0%   { transform: translate(0px, 0px) scale(1); }
  25%  { transform: translate(-6vw, -3vh) scale(1.04); }
  50%  { transform: translate(4vw, -5vh) scale(0.96); }
  75%  { transform: translate(7vw, 3vh) scale(1.03); }
  100% { transform: translate(0px, 0px) scale(1); }
`;

const BokehLayer = styled.div`
  ${FadeLayer};
  z-index: 2;
  overflow: hidden;
`;

const Bokeh = styled.div`
  position: absolute;
  left: var(--bokeh-left);
  top: var(--bokeh-top);
  width: var(--bokeh-size);
  height: var(--bokeh-size);
  border-radius: 999px;
  background: radial-gradient(
    circle at 40% 40%,
    var(--bokeh-color) 0%,
    rgba(0, 0, 0, 0) 70%
  );
  filter: blur(var(--bokeh-blur));
  animation: ${bokehFloat} var(--bokeh-dur) linear infinite;
  animation-delay: var(--bokeh-delay);
  mix-blend-mode: screen;
  opacity: 0.85;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* ------ Stars (night) ------ */
const twinkle = keyframes`
  0%   { opacity: 0.35; }
  50%  { opacity: 0.9; }
  100% { opacity: 0.45; }
`;

const StarsLayer = styled.div`
  ${FadeLayer};
  z-index: 1; /* behind bokeh, above gradient */
`;

const Star = styled.div`
  position: absolute;
  left: var(--star-left);
  top: var(--star-top);
  width: var(--star-size);
  height: var(--star-size);
  border-radius: 999px;
  background: radial-gradient(
    circle at 50% 50%,
    rgba(240, 248, 255, 0.95) 0%,
    rgba(240, 248, 255, 0.7) 45%,
    rgba(240, 248, 255, 0) 80%
  );
  opacity: var(--star-alpha);

  ${({ $twinkle }) =>
    $twinkle &&
    css`
      animation: ${twinkle} var(--twinkle-dur) ease-in-out infinite;
      animation-delay: var(--twinkle-delay);
      will-change: opacity;
    `}
`;

/* ------ Clouds (day) ------ */
const cloudDrift = keyframes`
  0%   { transform: translateX(var(--cloud-start)) scaleX(var(--cloud-scale)); }
  100% { transform: translateX(var(--cloud-end))   scaleX(var(--cloud-scale)); }
`;

const CloudsLayer = styled.div`
  ${FadeLayer};
  z-index: 1; /* same stack area as stars */
`;

const Cloud = styled.div`
  position: absolute;
  left: var(--cloud-left);
  top: var(--cloud-top);
  width: var(--cloud-w);
  height: var(--cloud-h);
  opacity: var(--cloud-alpha);
  filter: blur(var(--cloud-blur));
  will-change: transform, opacity;

  /* airy, fluffy ellipse */
  background: radial-gradient(
      ellipse at 30% 55%,
      rgba(255, 255, 255, 0.18) 0%,
      rgba(255, 255, 255, 0.1) 45%,
      rgba(255, 255, 255, 0) 75%
    ),
    radial-gradient(
      ellipse at 65% 45%,
      rgba(255, 255, 255, 0.16) 0%,
      rgba(255, 255, 255, 0.08) 40%,
      rgba(255, 255, 255, 0) 70%
    );

  animation: ${cloudDrift} var(--cloud-dur) linear infinite;
  animation-delay: var(--cloud-delay);
  animation-direction: alternate;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* Foreground content */
const Content = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3; /* above fixed layers */
  height: 100%;
  width: 100%;
`;

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

function useWindowWidth() {
  const [w, setW] = React.useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  React.useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return w;
}

/* ------- Generators ------- */
function makeBokeh(count, palette) {
  return Array.from({ length: count }).map((_, i) => {
    const r = (seed) => {
      const x = Math.sin(seed * 9999 + i * 77.77) * 43758.5453;
      return x - Math.floor(x);
    };

    const size = 120 + r(1) * 280;
    const blur = 8 + r(2) * 22;
    const left = r(4) * 100;
    const top = r(5) * 100;
    const driftDur = 28 + r(6) * 26;
    const driftDelay = -r(7) * 30;

    const nightBlue = "rgba(40, 70, 140, 0.22)";
    const nightEmber = "rgba(180, 90, 30, 0.18)";
    const dayBlue = "rgba(120, 170, 255, 0.20)";
    const dayEmber = "rgba(213, 234, 253, 0.16)";

    const blue = palette === "day" ? dayBlue : nightBlue;
    const ember = palette === "day" ? dayEmber : nightEmber;
    const huePick = r(3) < 0.5 ? blue : ember;

    const style = {
      "--bokeh-size": `${size}px`,
      "--bokeh-blur": `${blur}px`,
      "--bokeh-color": huePick,
      "--bokeh-left": `${left}vw`,
      "--bokeh-top": `${top}vh`,
      "--bokeh-dur": `${driftDur}s`,
      "--bokeh-delay": `${driftDelay}s`,
    };

    return <Bokeh key={`night-${i}`} style={style} />;
  });
}

function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), 1 | t);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeStars(count, seed = 123456) {
  const rand = mulberry32(seed);

  let minDistVW = 2.2;
  let minSq = (minDistVW / 100) ** 2;
  const pts = [];
  let attempts = 0;

  while (pts.length < count && attempts < count * 200) {
    const x = rand();
    const y = Math.pow(rand(), 1.35) * 0.75;

    let ok = true;
    for (let i = 0; i < pts.length; i++) {
      const dx = x - pts[i][0];
      const dy = y - pts[i][1];
      if (dx * dx + dy * dy < minSq) {
        ok = false;
        break;
      }
    }

    if (ok) {
      pts.push([x, y]);
    } else if (++attempts % (count * 20) === 0) {
      minDistVW *= 0.9;
      minSq = (minDistVW / 100) ** 2;
    }
  }

  return pts.map((p, i) => {
    const size = 1 + rand() * 3;
    const alpha = 0.35 + rand() * 0.4;
    const doTwinkle = rand() < 0.4;
    const dur = 2.4 + rand() * 3.6;
    const delay = -rand() * 6;

    const style = {
      "--star-left": `${p[0] * 100}vw`,
      "--star-top": `${p[1] * 100}vh`,
      "--star-size": `${size}px`,
      "--star-alpha": alpha,
      "--twinkle-dur": `${dur}s`,
      "--twinkle-delay": `${delay}s`,
    };

    return <Star key={`star-${i}`} style={style} $twinkle={doTwinkle} />;
  });
}

function makeClouds(count) {
  return Array.from({ length: count }).map((_, i) => {
    const r = (seed) => {
      const x = Math.sin(seed * 4242 + i * 19.19) * 24601.1357;
      return x - Math.floor(x);
    };

    // Position: mostly in top 0–40vh, some down to 70vh
    const deep = r(1) < 0.25;
    const top = deep ? 40 + r(2) * 30 : r(3) * 40;
    const left = r(4) * 100;

    // Size & feel: airy, wide, soft edges
    const w = 160 + r(5) * 340; // px
    const h = w * (0.28 + r(6) * 0.12);
    const blur = 10 + r(7) * 20;
    const alpha = 0.15 + r(8) * 0.15;

    // Gentle drift left to right with alternate to avoid teleports
    const dur = 60 + r(9) * 80; // 60–140s
    const delay = -r(10) * 60; // desync
    const shift = 8 + r(11) * 10; // vw travel each way
    const scaleX = 1.4 + r(12) * 0.8;

    const style = {
      "--cloud-left": `${left}vw`,
      "--cloud-top": `${top}vh`,
      "--cloud-w": `${w}px`,
      "--cloud-h": `${h}px`,
      "--cloud-blur": `${blur}px`,
      "--cloud-alpha": alpha,
      "--cloud-dur": `${dur}s`,
      "--cloud-delay": `${delay}s`,
      "--cloud-start": `-${shift}vw`,
      "--cloud-end": `${shift}vw`,
      "--cloud-scale": scaleX,
    };

    return <Cloud key={`cloud-${i}`} style={style} />;
  });
}

/* ------- Component ------- */
export default function FluidBackground({
  count = 18, // night bokeh count
  normalHeight,
  isDay = false,
  parallax = true,
  nightStars = 100, // star density control
  dayClouds = 24, // cloud density control
  children,
}) {
  const width = useWindowWidth();

  // Scale star / cloud counts for mobile to prevent clutter
  const densityScale = React.useMemo(
    () => clamp(width / 1200, 0.5, 1),
    [width]
  );

  const effectiveStarCount = React.useMemo(
    () => Math.max(8, Math.round(nightStars * densityScale)),
    [nightStars, densityScale]
  );
  const effectiveCloudCount = React.useMemo(
    () => Math.max(6, Math.round(dayClouds * densityScale)),
    [dayClouds, densityScale]
  );

  const seedRef = React.useRef(Math.floor(Math.random() * 2 ** 31));

  const stars = React.useMemo(
    () => makeStars(effectiveStarCount, seedRef.current),
    [effectiveStarCount]
  );

  const clouds = React.useMemo(
    () => makeClouds(effectiveCloudCount),
    [effectiveCloudCount]
  );

  const nightDots = React.useMemo(() => makeBokeh(count, "night"), [count]);

  return (
    <FluidBg $normalHeight={normalHeight}>
      {/* Gradients cross-fade */}
      <GradientNight $show={!isDay} $parallax={parallax} />
      <GradientDay $show={isDay} $parallax={parallax} />

      {/* Stars (night only) */}
      <StarsLayer aria-hidden="true" $show={!isDay} $parallax={parallax}>
        {stars}
      </StarsLayer>

      {/* Clouds (day only) */}
      <CloudsLayer aria-hidden="true" $show={isDay} $parallax={parallax}>
        {clouds}
      </CloudsLayer>

      {/* Night bokeh */}
      <BokehLayer aria-hidden="true" $show={!isDay} $parallax={parallax}>
        {nightDots}
      </BokehLayer>

      <Content>{children}</Content>
    </FluidBg>
  );
}
